
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  Share2, 
  Twitter, 
  Facebook, 
  Linkedin, 
  Mail, 
  Copy, 
  Check,
  MessageCircle
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";

interface ShareButtonsProps {
  url?: string;
  title?: string;
  description?: string;
  showLabel?: boolean;
  size?: "sm" | "default" | "lg";
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
}

export const ShareButtons = ({ 
  url = window.location.href, 
  title = "Plateforme Citoyenne - Signalement", 
  description = "Consultez ce signalement sur la Plateforme Citoyenne",
  showLabel = true,
  size = "default",
  variant = "default" 
}: ShareButtonsProps) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    
    toast({
      title: "Lien copié",
      description: "Le lien a été copié dans votre presse-papier.",
    });
    
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className="flex items-center gap-1"
        >
          <Share2 className="h-4 w-4" />
          {showLabel && <span className="hidden sm:inline-block">Partager</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3">
        <h3 className="text-sm font-medium mb-2">Partager ce signalement</h3>
        <div className="grid grid-cols-3 gap-2">
          <Button
            onClick={() => window.open(shareLinks.twitter, '_blank')}
            variant="outline"
            size="sm"
            className="flex flex-col items-center justify-center h-14 p-1"
          >
            <Twitter className="h-5 w-5 mb-1 text-blue-400" />
            <span className="text-xs">Twitter</span>
          </Button>
          
          <Button
            onClick={() => window.open(shareLinks.facebook, '_blank')}
            variant="outline"
            size="sm"
            className="flex flex-col items-center justify-center h-14 p-1"
          >
            <Facebook className="h-5 w-5 mb-1 text-blue-600" />
            <span className="text-xs">Facebook</span>
          </Button>
          
          <Button
            onClick={() => window.open(shareLinks.linkedin, '_blank')}
            variant="outline"
            size="sm"
            className="flex flex-col items-center justify-center h-14 p-1"
          >
            <Linkedin className="h-5 w-5 mb-1 text-blue-700" />
            <span className="text-xs">LinkedIn</span>
          </Button>
          
          <Button
            onClick={() => window.open(shareLinks.email, '_blank')}
            variant="outline"
            size="sm"
            className="flex flex-col items-center justify-center h-14 p-1"
          >
            <Mail className="h-5 w-5 mb-1 text-gray-600" />
            <span className="text-xs">Email</span>
          </Button>
          
          <Button
            onClick={() => window.open(shareLinks.whatsapp, '_blank')}
            variant="outline"
            size="sm"
            className="flex flex-col items-center justify-center h-14 p-1"
          >
            <MessageCircle className="h-5 w-5 mb-1 text-green-500" />
            <span className="text-xs">WhatsApp</span>
          </Button>
          
          <Button
            onClick={copyToClipboard}
            variant="outline"
            size="sm"
            className="flex flex-col items-center justify-center h-14 p-1"
          >
            {copied ? (
              <Check className="h-5 w-5 mb-1 text-green-500" />
            ) : (
              <Copy className="h-5 w-5 mb-1" />
            )}
            <span className="text-xs">Copier</span>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareButtons;
